import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { RadioButton } from "@tql/ui-components-react/kendo/inputs";
import { Card } from "@tql/ui-components-react/kendo/layout";
import {
	creditCard_01,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";

import { PaymentErrorModal } from "~/features/payment/error-modal/index.tsx";
import { useGetCustomerAccounts } from "~/features/payment/hooks/use-get-customer-accounts.ts";
import type { PaymentNonce, SavedCard } from "~/shared/types/payment-types";

import { useDeletePaymentMethod } from "../../hooks/use-delete-payment-method.ts";

type SavedCCCardProps = {
	handleApplyPaymentMethod: (paymentMethod: SavedCard | PaymentNonce) => void;
	savedCard: SavedCard | PaymentNonce;
	hideSelectionActions?: boolean;
	isSelected?: boolean;
};

export const SavedCCCard = ({
	handleApplyPaymentMethod,
	savedCard,
	isSelected = false,
}: SavedCCCardProps) => {
	const { accounts } = useGetCustomerAccounts();
	const { deletePaymentMethodAsync, isSubmitting, error, reset } =
		useDeletePaymentMethod();
	const cardType =
		"cardType" in savedCard ? (savedCard.cardType ?? "Card") : "Card";

	const getSafeLastFour = () => {
		if ("Last4" in savedCard && savedCard.Last4) return savedCard.Last4;
		if ("lastFour" in savedCard && savedCard.lastFour)
			return savedCard.lastFour;
		if ("cardNumberLast4" in savedCard && savedCard.cardNumberLast4)
			return savedCard.cardNumberLast4;
		return "####";
	};

	const safeLastFour = getSafeLastFour();

	let token = "new-card";
	if ("paymentMethodToken" in savedCard && savedCard.paymentMethodToken) {
		token = savedCard.paymentMethodToken;
	} else if ("nonce" in savedCard) {
		token = savedCard.nonce;
	}

	return (
		<>
			<PaymentErrorModal
				errorMessage={
					"An error occurred while deleting the payment method. Please try again."
				}
				isOpen={!!error}
				onClose={reset}
			/>
			<Card
				onClick={() => handleApplyPaymentMethod(savedCard)}
				style={{
					padding: "10px",
					marginBottom: "10px",
					cursor: "pointer",
				}}
			>
				{isSubmitting ? (
					<Loader size={"large"} type={"infinite-spinner"} />
				) : (
					<div
						data-testid={"payment-method-row"}
						style={{ display: "flex", alignItems: "center" }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<RadioButton
								checked={isSelected}
								onChange={() => handleApplyPaymentMethod(savedCard)}
							>
								<SvgIcon
									icon={creditCard_01}
									size={"large"}
									style={{ marginLeft: "8px" }}
								/>
							</RadioButton>
						</div>

						<div
							key={`payment-card-${token}`}
							style={{ flex: 1, textAlign: "center" }}
						>
							<h6>
								{cardType} **** {safeLastFour}
							</h6>
							{"isDefault" in savedCard && savedCard.isDefault ? (
								<div style={{ marginLeft: "auto" }}>
									<p>Default Credit Card</p>
								</div>
							) : null}
						</div>
						<SvgIcon
							icon={trash_01}
							onClick={(e) => {
								e.stopPropagation();
								void deletePaymentMethodAsync({
									paymentMerchantCustomerId:
										accounts?.paymentMerchantCustomerId ?? "",
									paymentMethodToken: savedCard.paymentMethodToken ?? "",
								});
							}}
							size={"xlarge"}
							style={{ color: "var(--kendo-color-error-on-surface)" }}
						/>
					</div>
				)}
			</Card>
		</>
	);
};
